import { Stack } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { colors } from 'styles/colors';
import IndividualFeedback from './individualFeedback';
import CandidateFeedbackButton from './candidateFeedbackButton';
import {
  ICandidateFeedbackListDetails,
  ICandidateFeedbackListResponse,
} from 'modules/hiring/models/candidateFeedbackListDetails.model';
import IndividualFeedbackShimmer from './individualFeedbackShimmer';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { ICandidateFeedbackInterviwerCount } from 'modules/hiring/models/candidateFeedbackListDetails.model';
import {
  FeedbackHiringDecision,
  InterviewFeedbackQuestionType,
} from 'models/enum';

export interface ICandidateFeedbackList {
  feedbackList: ICandidateFeedbackListResponse | undefined;
  showLoading: boolean;
}
const CandidateFeedbackList = ({
  feedbackList,
  showLoading,
}: ICandidateFeedbackList) => {
  const [buttonFilter, setButtonFilter] = useState<string>('');
  const [candidateFeedbackCount, setCandidateFeedbackCount] =
    useState<ICandidateFeedbackInterviwerCount>({
      yes: 0,
      no: 0,
      notSure: 0,
    });
  const handleChange = (buttonSelected: string) => {
    setButtonFilter(buttonFilter === buttonSelected ? '' : buttonSelected);
  };
  useEffect(() => {
    const newCandidateFeedbackCount = { ...candidateFeedbackCount };
    feedbackList?.individualResponseModelList.forEach((feedbackItem) => {
      feedbackItem?.response?.responseItemList?.forEach((responseItem) => {
        if (
          responseItem?.questionType === InterviewFeedbackQuestionType.YES_NO_NA
        ) {
          newCandidateFeedbackCount[responseItem.selectedOption] =
            (newCandidateFeedbackCount[responseItem.selectedOption] || 0) + 1;
        }
      });
    });
    setCandidateFeedbackCount(newCandidateFeedbackCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackList]);

  const getFilterList = () => {
    return buttonFilter === ''
      ? feedbackList?.individualResponseModelList
      : feedbackList?.individualResponseModelList?.filter((feedbackItem) => {
          return feedbackItem?.response?.responseItemList?.some(
            (responseItem) => responseItem?.selectedOption === buttonFilter
          );
        });
  };
  const shimmerCount = 3;

  return (
    <Stack
      spacing={1.25}
      id='detailed-feedback-container'
      data-testid='detailed-feedback-container'
    >
      <Stack
        direction='row'
        sx={{ display: 'flex', flexWrap: 'wrap', marginRight: '10px' }}
      >
        <CandidateFeedbackButton
          buttonSx={{ margin: '3px 10px 3px 0px' }}
          variant='recommends'
          count={candidateFeedbackCount.yes}
          id={FeedbackHiringDecision.RECOMMENDS}
          handleClick={handleChange}
          isDisabled={
            !!(
              (buttonFilter &&
                buttonFilter !== FeedbackHiringDecision.RECOMMENDS) ||
              candidateFeedbackCount.yes === 0
            )
          }
        />
        <CandidateFeedbackButton
          buttonSx={{ margin: '3px 10px 3px 0px' }}
          variant='doesntRecommend'
          count={candidateFeedbackCount.no}
          id={FeedbackHiringDecision.DOESNTRECOMMEND}
          handleClick={handleChange}
          isDisabled={
            !!(
              (buttonFilter &&
                buttonFilter !== FeedbackHiringDecision.DOESNTRECOMMEND) ||
              candidateFeedbackCount.no === 0
            )
          }
        />
        <CandidateFeedbackButton
          buttonSx={{ margin: '3px 10px 3px 0px' }}
          variant='notSure'
          count={candidateFeedbackCount.notSure}
          id={FeedbackHiringDecision.NOTSURE}
          handleClick={handleChange}
          isDisabled={
            !!(
              buttonFilter && buttonFilter !== FeedbackHiringDecision.NOTSURE
            ) || candidateFeedbackCount.notSure === 0
          }
        />
      </Stack>
      <Stack
        gap={1.25}
        sx={{
          maxHeight: ({ typography }) => typography.pxToRem(881),
          overflowY: 'auto',
          pr: '0.5rem',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '0.375rem',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            background: colors.scrollBar.lightBackground,
            borderRadius: '0.188rem',
          },
        }}
      >
        {showLoading ? (
          <>
            {[...Array(shimmerCount)].map((_, i) => (
              <IndividualFeedbackShimmer
                key={`individual-feedback-shimmer-${i}`}
              />
            ))}
          </>
        ) : !feedbackList?.individualResponseModelList?.length ? (
          <TypographyWithI18N
            i18nKey='noData'
            id='no-data'
            sx={{ textAlign: 'center', mt: '1rem' }}
          />
        ) : (
          getFilterList()?.map(
            (data: ICandidateFeedbackListDetails, i: number) => {
              return (
                <IndividualFeedback
                  key={`individual-feedback-${i}`}
                  feedbackData={data}
                  id={i}
                />
              );
            }
          )
        )}
      </Stack>
    </Stack>
  );
};

export default CandidateFeedbackList;
